import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { FundingNeedResponse } from '@/models/FundingNeeds'
import UpgradeCTA from '../UpgradeCTA'
import { AccessibleTooltip } from '../AccessibleTooltip'
import { useQueryClient } from '@tanstack/react-query'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useScanTask } from '@/utils/tasks/scanTask'
import { useAdoptActiveScan } from '@/utils/tasks/useAdoptActiveScan'
import { useFundingNeedStaleness } from '@/utils/hooks/useFundingNeedStaleness'
import {
  ScanStatusAnnouncer,
  scanAnnouncement,
} from '../ScanStatusAnnouncer'
import { ScanBlockedTooltip } from '../ScanBlockedTooltip'

const STAGED_UPLOAD_SCAN_CONFIRM =
  "You have a file or website that hasn't been uploaded yet. Matching won't include it until it's uploaded. Are you sure you want to start matching now?"

interface FundingNeedMatchActionsProps {
  token: string
  fundingNeed: FundingNeedResponse
  scanIds?: number[]
  isAnnualSubscriber?: boolean
  hasPendingUpload?: boolean
}

export default function FundingNeedMatchActions({
  token,
  fundingNeed,
  scanIds,
  hasPendingUpload = false,
}: FundingNeedMatchActionsProps) {
  const includeRelational = true
  const [isStartingScan, setIsStartingScan] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState('')
  const queryClient = useQueryClient()
  const { user } = getCurrentUser()

  const {
    hasResults,
    canStartScan,
    scanBlockedReason,
    viewResultsDisabled,
    showStaleWarning,
    latestScanFoundNothing,
  } = useFundingNeedStaleness(token, fundingNeed, scanIds)

  const needId = fundingNeed.id ? String(fundingNeed.id) : ''

  // Without this the row only knows about scans this browser started: one started
  // on another device, by a colleague or during onboarding renders as an idle
  // "Re-find Matches" button that POSTs straight into a 409.
  useAdoptActiveScan(token as string, Number(fundingNeed.id))

  const {
    startScan,
    isLoadingForNeed,
    progressForNeed,
    latestStatusForNeed,
    hasActiveScan,
    errorForNeed,
    isScanStaleForNeed,
  } = useScanTask({
    token: token as string,
    // Fires for every scan that finishes, not just this row's.
    onCompleted: (_results, _taskId, completedNeedId) => {
      if (completedNeedId === String(fundingNeed.id)) setIsStartingScan(false)
      queryClient.invalidateQueries({
        queryKey: ['scanIds', fundingNeed.id],
      })
      // The scan just spent part of the org's allowance; without this the cap
      // check below keeps reading the pre-scan count.
      queryClient.invalidateQueries({
        queryKey: ['orgScanUsage'],
      })
      if (user?.uuid) {
        queryClient.invalidateQueries({
          queryKey: ['onboardingData', user.uuid, false, fundingNeed.org_uuid],
        })
      }
    },
    // Fires for every scan that fails, not just this row's.
    onError: (error, _taskId, erroredNeedId) => {
      if (erroredNeedId !== String(fundingNeed.id)) return
      setIsStartingScan(false)
      if (error.name === 'PaymentRequired') {
        setUpgradeReason(error.message)
        setShowUpgradeDialog(true)
      }
    },
  })

  const isStale = needId ? isScanStaleForNeed(needId) : false
  const isScanning = needId
    ? (hasActiveScan(needId) || isLoadingForNeed(needId) || isStartingScan) &&
      !isStale
    : false
  const scanProgress = needId ? progressForNeed(needId) || 0 : 0
  const isScanComplete = needId
    ? latestStatusForNeed(needId) === 'completed'
    : false
  const scanError = needId ? errorForNeed(needId) : null

  async function handleRunScan(includeFuturePlanning = false) {
    if (!token || !needId) return
    // The scan buttons are disabled with this reason; this also stops a
    // click on the span-rendered buttons, which `disabled` does not.
    if (scanBlockedReason) return
    if (hasPendingUpload && !window.confirm(STAGED_UPLOAD_SCAN_CONFIRM)) {
      return
    }

    try {
      setIsStartingScan(true)
      const jobId = await startScan(
        needId,
        includeRelational,
        includeFuturePlanning,
      )
      if (!jobId) {
        setIsStartingScan(false)
      }
    } catch {
      setIsStartingScan(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Column: View Results - fixed width, reserved on every row so the
          action column after it lines up across rows regardless of state. */}
      <Box sx={{ width: '130px', flexShrink: 0 }}>
        {hasResults &&
          (viewResultsDisabled ? (
            <AccessibleTooltip
              title="Please click Re-find Matches button to see latest matches"
              arrow
              placement="top"
            >
              <span style={{ display: 'inline-block', width: '100%' }}>
                <Button
                  variant="outlined"
                  disableRipple
                  size="small"
                  disabled
                  fullWidth
                  // Rendered as a span, not a native button: this whole row
                  // lives inside the AccordionSummary's button, and a nested
                  // <button> is invalid DOM. Same pattern as Find Matches below.
                  component="span"
                  role="button"
                  tabIndex={0}
                  aria-label={`View results for ${fundingNeed.title} (disabled — results are out of date)`}
                  sx={{
                    color: '#1C3045',
                    borderColor: '#1C3045',
                    textTransform: 'none',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  View Results
                </Button>
              </span>
            </AccessibleTooltip>
          ) : (
            <Button
              variant="outlined"
              disableRipple
              size="small"
              fullWidth
              component={Link}
              to={`/match/${fundingNeed.id}`}
              aria-label={`View results for ${fundingNeed.title}`}
              onClick={(event) => event.stopPropagation()}
              sx={{
                color: '#1C3045',
                borderColor: '#1C3045',
                textTransform: 'none',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
            >
              View Results
            </Button>
          ))}
      </Box>

      {/* Column: primary action/status - fixed width, reserved on every row. */}
      <Box sx={{ width: '160px', flexShrink: 0 }}>
        {isScanning ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: '#1C3045',
                fontSize: '14px',
              }}
            >
              <CircularProgress size={16} sx={{ color: '#1C3045', mr: 1 }} />
              Matching...
            </Typography>
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={scanProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#E0E0E0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#1C3045',
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  color: '#666',
                  mt: 0.25,
                }}
              >
                {scanProgress}%
              </Typography>
            </Box>
          </Box>
        ) : scanError ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Alert severity="error" sx={{ fontSize: '13px', py: 0 }}>
              Sorry, something went wrong!
            </Alert>
            <ScanBlockedTooltip reason={scanBlockedReason}>
              <Button
                variant="contained"
                disableRipple
                size="small"
                fullWidth
                component="span"
                role="button"
                tabIndex={0}
                aria-label={`Try scanning again for ${fundingNeed.title}`}
                onClick={(event) => {
                  event.stopPropagation()
                  handleRunScan(false)
                }}
                disabled={!canStartScan}
                sx={{
                  fontSize: '14px',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  color: 'white',
                  bgcolor: '#1C3045',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#162433' },
                }}
              >
                Try Again
              </Button>
            </ScanBlockedTooltip>
          </Box>
        ) : hasResults && showStaleWarning ? (
          <ScanBlockedTooltip reason={scanBlockedReason}>
            <Button
              variant="contained"
              disableRipple
              size="small"
              fullWidth
              component="span"
              role="button"
              tabIndex={0}
              aria-label={`Re-find matches for ${fundingNeed.title}`}
              onClick={(event) => {
                event.stopPropagation()
                handleRunScan(false)
              }}
              disabled={!canStartScan}
              sx={{
                fontSize: '14px',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                color: 'white',
                bgcolor: '#1C3045',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#162433' },
              }}
            >
              Re-find Matches
            </Button>
          </ScanBlockedTooltip>
        ) : latestScanFoundNothing && !showStaleWarning ? (
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: '#1C3045',
              fontSize: '14px',
            }}
          >
            <InfoOutlinedIcon
              sx={{ color: '#1C3045', mr: 0.5, fontSize: '18px' }}
            />
            No matches found
          </Typography>
        ) : hasResults || (isScanComplete && !showStaleWarning) ? (
          // A just-finished scan reads as complete only while the request is still
          // current; once it's edited or goes stale the scan button comes back.
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: '#1C3045',
              fontSize: '14px',
            }}
          >
            <TaskAltIcon sx={{ color: '#1C3045', mr: 0.5, fontSize: '18px' }} />
            Scan Complete
          </Typography>
        ) : (
          <ScanBlockedTooltip reason={scanBlockedReason}>
            <Button
              variant="contained"
              disableRipple
              size="small"
              fullWidth
              component="span"
              role="button"
              tabIndex={0}
              aria-label={`Find matches for ${fundingNeed.title}`}
              onClick={(event) => {
                event.stopPropagation()
                handleRunScan(false)
              }}
              disabled={!canStartScan}
              sx={{
                fontSize: '14px',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                color: 'white',
                bgcolor: '#1C3045',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#162433' },
              }}
            >
              Find Matches
            </Button>
          </ScanBlockedTooltip>
        )}
      </Box>

      <ScanStatusAnnouncer
        message={scanAnnouncement({
          title: fundingNeed.title,
          isScanning,
          scanError,
          latestStatus: needId ? latestStatusForNeed(needId) : 'idle',
          latestScanFoundNothing,
        })}
      />

      <UpgradeCTA
        variant="dialog"
        isOpen={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        message={
          upgradeReason ||
          'Free tier users are limited to 2 scans. Please upgrade to continue.'
        }
      />
    </Box>
  )
}
